import React from "react";
import { usePassionsData, PASSION_DEFINITIONS } from "~/hooks/usePassionsData";
import { usePassionsStore } from "~/store";
import { getAnalyticsClickTrackingAttributes } from "~/utils/adobeDataLayerUtils";
import { Carousel, ContentCard, ContentCardSkeleton } from "~/components";
import momentsData from "~/data/momentsData.json";

export const MomentPassion = () => {
  const { getSelectedPassions, loading } = usePassionsData();
  const storeSelectedPassions = usePassionsStore(
    (state) => state.selectedPassions,
  );

  const cdpPassions = getSelectedPassions();
  const selectedPassions =
    storeSelectedPassions && storeSelectedPassions.length > 0
      ? storeSelectedPassions
      : cdpPassions;

  const handleMomentClick = (moment) => {
    console.log("Moment clicked:", moment);
    // Open the moment link if available
    if (moment?.href) {
      const url = moment.href.startsWith("http")
        ? moment.href
        : `https://moments.marriottbonvoy.com${moment.href}`;
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  // Shuffle array utility function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Get all available passions from the moments data
  const getAllAvailablePassions = () => {
    return Object.keys(momentsData);
  };

  // Generate moments based on user's passions
  const generatePassionMoments = () => {
    const allPassions = getAllAvailablePassions();
    let momentsToShow = [];

    if (selectedPassions.length === 0) {
      // No passions selected - pick 3 random passions and 1 moment from each
      const randomPassions = shuffleArray(allPassions).slice(0, 3);
      randomPassions.forEach((passion) => {
        if (momentsData[passion]?.moments?.length > 0) {
          const moment = momentsData[passion].moments[0];
          momentsToShow.push({ passion, moment });
        }
      });
    } else if (selectedPassions.length === 1) {
      // Single passion - show just 1 moment group for THIS passion (single card)
      const knownPassion = selectedPassions[0];
      // Normalize key to match data (lowercase keys in JSON)
      const dataKey = knownPassion.toLowerCase();
      const existingMoments = momentsData[dataKey]?.moments || [];

      if (existingMoments.length > 0) {
        // Show just the one available moment
        momentsToShow.push({
          passion: knownPassion,
          moment: existingMoments[0],
        });
      } else {
        // Logic for "If user does not exist in CDP... act like NO values".
        // If passion exists but no data, we fallback to popular behavior (random from all).
        const randomPassions = shuffleArray(allPassions).slice(0, 3);
        randomPassions.forEach((passion) => {
          if (momentsData[passion]?.moments?.length > 0) {
            const moment = momentsData[passion].moments[0];
            momentsToShow.push({ passion, moment });
          }
        });
      }
    } else {
      // Multiple passions - 1 moment for each passion (up to 6)
      const passionsToUse = selectedPassions.slice(0, 6);

      passionsToUse.forEach((passion) => {
        if (momentsData[passion]?.moments?.length > 0) {
          const moment = momentsData[passion].moments[0];
          momentsToShow.push({ passion, moment });
        }
      });
    }

    // Randomize the final order
    return shuffleArray(momentsToShow);
  };

  // Convert passion moments to MomentCard format
  const convertToMomentCards = (passionMomentData) => {
    return passionMomentData.map(({ passion, moment }) => ({
      ...moment,
      onClick: (e) => {
        e.preventDefault();
        handleMomentClick(moment);
      },
      passionType: passion,
    }));
  };

  // Show loading state with skeleton cards
  if (loading) {
    const skeletonCount = 3;

    return (
      <div style={{}}>
        <Carousel
          title="Find Your Marriott Bonvoy Moment"
          showNavigation={true}
          gap={32}
          navButtonTop="130px"
        >
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <ContentCardSkeleton key={`skeleton-${index}`} />
          ))}
        </Carousel>
      </div>
    );
  }

  // Generate the moments
  const passionMomentData = generatePassionMoments();
  const momentCards = convertToMomentCards(passionMomentData);

  const isSingleSlide = momentCards.length === 1;

  // Render MomentCard with style props for single/multi card layout
  return (
    <div style={{}}>
      <Carousel
        title="Find Your Marriott Bonvoy Moment"
        showNavigation={true}
        gap={32}
        navButtonTop="130px"
      >
        {momentCards.map((moment) => {
          const cardStyle = isSingleSlide
            ? { width: "830px", maxWidth: "calc(100vw - 48px)" }
            : { width: "280px" };

          const imageStyle = isSingleSlide
            ? { height: "440px", width: "100%", objectFit: "cover" }
            : { height: "320px", width: "100%", objectFit: "cover" };

          // Resolve full URL
          const fullUrl = moment.href
            ? moment.href.startsWith("http")
              ? moment.href
              : `https://moments.marriottbonvoy.com${moment.href}`
            : null;

          // Prepare activities badge
          const passionDef = PASSION_DEFINITIONS[moment.passionType];
          const activities =
            passionDef && passionDef.icon
              ? [
                  {
                    label: passionDef.label,
                    icon: passionDef.icon,
                  },
                ]
              : [];

          return (
            <ContentCard
              key={moment.id}
              title={moment.title}
              description={moment.description}
              image={moment.image}
              style={cardStyle}
              imageStyle={imageStyle}
              onClick={moment.onClick}
              hotelLink={
                fullUrl
                  ? {
                      url: fullUrl,
                      target: "_blank",
                      rel: "noopener noreferrer",
                    }
                  : undefined
              }
              {...getAnalyticsClickTrackingAttributes(
                "Moment Cards",
                "click",
                `Opened ${moment.id}`,
              )}
            />
          );
        })}
      </Carousel>
    </div>
  );
};
